import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { CollapseBlock, Spinner } from '../../../../../common/index.jsx';
import FoDatesForm from './FoDatesForm.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import {
  useGetCollidedDatesMutation,
  useGetDateQuery,
  useGetDatesQuery,
  useSaveDateMutation,
} from '../../../../../../redux/services/fo_dates.jsx';
import { useGetWorkshopQuery } from '../../../../../../redux/services/fo_workshops.jsx';
import { useGetTeachersQuery } from '../../../../../../redux/services/users.jsx';
import { Form as AntdForm } from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { DATETIME_FORMAT_DATETIME_DB } from '../../../../../../utils/helpers/CommonHelper';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';

const FoDatesEdit = ({ mode }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dateId = params.dateId;
  const workshopId = params.workshopId;
  const id = dateId || workshopId;
  const copyFromDateId = searchParams.get('copyFromDateId');

  const [form] = AntdForm.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const currentLecturerId =
    user?.person_id === null || user?.person_id === undefined
      ? user?.id === null || user?.id === undefined
        ? undefined
        : String(user.id)
      : String(user.person_id);

  const { saveSuccess, errorMessage } = useToastMessages();

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  const { data: date, isLoading: isLoadingDate } = useGetDateQuery(dateId, {
    skip: isCreate,
  });

  const { data: copiedDate, isLoading: isLoadingCopiedDate } = useGetDateQuery(
    copyFromDateId,
    {
      skip: !isCreate || !copyFromDateId,
    },
  );

  const { data: workshop, isLoading: isLoadingWorkshop } = useGetWorkshopQuery(
    workshopId,
    {
      skip: isEdit || !workshopId,
    },
  );

  const { data: teachers, isLoading: isLoadingTeachers } =
    useGetTeachersQuery();

  const [saveDate, { isSuccess: isSaveSuccess }] = useSaveDateMutation();

  const { refetch } = useGetDatesQuery(id);
  const [getCollidedDates] = useGetCollidedDatesMutation();

  const title = isCreate ? 'Pridanie termínu' : 'Úprava termínu';
  const copiedDateName = copiedDate?.name
    ? // stored data, kept in Slovak regardless of the UI language
      `${copiedDate.name} (kópia)`
    : undefined;

  const initials = {
    name: undefined,
    note: undefined,
    lecturer_id:
      date?.lecturer_id !== null && date?.lecturer_id !== undefined
        ? String(date.lecturer_id)
        : currentLecturerId,
    location: undefined,
    capacity: undefined,
    ...(isCreate ? copiedDate : {}),
    ...(isCreate && copiedDateName ? { name: copiedDateName } : {}),
    ...date,
    date_from: date?.date_from
      ? dayjs(date.date_from)
      : copiedDate?.date_from
        ? dayjs(copiedDate.date_from)
        : null,
    date_to: date?.date_to
      ? dayjs(date.date_to)
      : copiedDate?.date_to
        ? dayjs(copiedDate.date_to)
        : null,
  };

  const eventDateFrom =
    date?.workshop?.event?.date_from ?? workshop?.event?.date_from;
  const eventDateTo =
    date?.workshop?.event?.date_to ?? workshop?.event?.date_to;

  const date_from = AntdForm.useWatch('date_from', form);
  const date_to = AntdForm.useWatch('date_to', form);

  const [collidedDates, setCollidedDates] = useState([]);

  const teachersForSelect = useMemo(() => {
    return teachers
      ?.map((person) => ({
        label: person.full_name ?? person.fullName ?? '',
        value: String(person.person_id ?? person.id),
        last_name: person.last_name ?? person.person?.last_name ?? '',
      }))
      .sort((a, b) =>
        (a.last_name || a.label).localeCompare(b.last_name || b.label),
      );
  }, [teachers]);

  useEffect(() => {
    if (isSaveSuccess) {
      saveSuccess();
      navigate(-1);
      refetch();
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isEdit && date?.workshop?.event?.archived_at) {
      errorMessage({
        message: t('Termín v archivovanej akcii nie je možné upravovať.'),
      });
      navigate(routes.cmsFoDatesDetail(id), { replace: true });
    }
  }, [
    date?.workshop?.event?.archived_at,
    errorMessage,
    id,
    isEdit,
    navigate,
    t,
  ]);

  useEffect(() => {
    if (isCreate && workshop?.event?.archived_at) {
      errorMessage({
        message: t('Do archivovanej akcie nie je možné pridávať termíny.'),
      });
      navigate(routes.cmsFoDatesList(id), { replace: true });
    }
  }, [errorMessage, id, isCreate, navigate, t, workshop?.event?.archived_at]);

  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue({
        date_from: dayjs(date?.date_from),
        date_to: dayjs(date?.date_to),
      });
    } else {
      if (copiedDate) {
        form.setFieldsValue({
          ...copiedDate,
          lecturer_id: currentLecturerId,
          name: copiedDateName,
          date_from: copiedDate?.date_from ? dayjs(copiedDate.date_from) : null,
          date_to: copiedDate?.date_to ? dayjs(copiedDate.date_to) : null,
        });
        return;
      }

      const defaultEventStart = eventDateFrom
        ? dayjs(eventDateFrom).startOf('day').hour(8).minute(0)
        : dayjs().startOf('day').hour(8).minute(0);

      form.setFieldsValue({
        lecturer_id: currentLecturerId,
        date_from: defaultEventStart,
        date_to: defaultEventStart,
      });
    }
  }, [
    isEdit,
    date,
    isCreate,
    form,
    eventDateFrom,
    copiedDate,
    currentLecturerId,
    copiedDateName,
  ]);

  useEffect(() => {
    if (date_from && date_to) {
      const fetchCollidedSubjects = async () => {
        try {
          const result = await getCollidedDates({
            ...(isEdit && dateId ? { id: Number(dateId) } : {}),
            date_from: dayjs(date_from).format(DATETIME_FORMAT_DATETIME_DB),
            date_to: dayjs(date_to).format(DATETIME_FORMAT_DATETIME_DB),
          }).unwrap();
          setCollidedDates(result);
        } catch (error) {
          //apiErrorMessage(error);
        }
      };
      fetchCollidedSubjects();
    }
  }, [date_from, date_to, getCollidedDates, isEdit, dateId]);

  return (isEdit && isLoadingDate) ||
    isLoadingTeachers ||
    isLoadingWorkshop ||
    isLoadingCopiedDate ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t(title)}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        permissionsBasic={'fo-dates'}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock title={t('Obsah')}>
          <FoDatesForm
            initials={initials}
            teachers={teachersForSelect || []}
            form={form}
            isEdit={isEdit}
            collidedDates={collidedDates}
            eventDateFrom={eventDateFrom}
            eventDateTo={eventDateTo}
            onSubmit={(values) => {
              saveDate({
                data: {
                  ...values,
                  date_from: dayjs(values.date_from).format('YYYY-MM-DDTHH:mm'),
                  date_to: dayjs(values.date_to).format('YYYY-MM-DDTHH:mm'),
                },
                ...(isEdit && { dateId: id }),
                ...(isCreate && { workshopId: id }),
              });
            }}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default FoDatesEdit;
